import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Upload, FileText, Image as ImageIcon, Trash2, Download, Send, Sparkles, Bot, User as UserIcon, Milestone, Loader2 } from "lucide-react";

const TYPE_COLOR = {
  hak_cipta: "bg-emerald-600", paten: "bg-blue-600", merek: "bg-[#D97706]",
  desain_industri: "bg-violet-600", dtlst: "bg-rose-600",
};

const SUGGESTIONS = [
  "Bagaimana strategi menulis klaim independen untuk invensi saya?",
  "Analisis prior art umum di bidang ini dan bagaimana saya bisa membuktikan novelty?",
  "Apakah invensi saya termasuk paten atau paten sederhana? Jelaskan bedanya.",
  "Cek kelayakan klaim: apakah rumusan saya cukup jelas untuk lolos pemeriksaan substantif?",
  "Rekomendasikan strategi respon jika klaim ditolak DJKI.",
];

// simple markdown-lite renderer
function md(text = "") {
  return text
    .split("\n")
    .map((line, i) => {
      if (line.startsWith("# ")) return <h3 key={i} className="serif text-lg font-bold text-[#0F2922] mt-3">{line.slice(2)}</h3>;
      if (line.startsWith("## ")) return <h4 key={i} className="font-semibold text-[#0F2922] mt-3">{line.slice(3)}</h4>;
      if (line.startsWith("### ")) return <h5 key={i} className="font-semibold text-[#0F2922] mt-2">{line.slice(4)}</h5>;
      if (line.startsWith("- ")) return <li key={i} className="ml-5 list-disc">{formatInline(line.slice(2))}</li>;
      if (/^\d+\.\s/.test(line)) return <li key={i} className="ml-5 list-decimal">{formatInline(line.replace(/^\d+\.\s/, ""))}</li>;
      if (line.trim() === "") return <div key={i} className="h-2"/>;
      return <p key={i}>{formatInline(line)}</p>;
    });
}
function formatInline(t) {
  const parts = t.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**")) return <b key={i}>{p.slice(2, -2)}</b>;
    if (p.startsWith("`")) return <code key={i} className="mono bg-slate-100 px-1 rounded text-xs">{p.slice(1, -1)}</code>;
    return p;
  });
}

export default function HakiDetail() {
  const { id } = useParams();
  const [types, setTypes] = useState({});
  const [haki, setHaki] = useState(null);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("kt_token") : "";

  const load = async () => {
    const [t, h, f, m] = await Promise.all([
      http.get("/haki/types"),
      http.get(`/haki/${id}`),
      http.get(`/haki/${id}/files`),
      http.get(`/haki/${id}/chat`),
    ]);
    setTypes(t.data); setHaki(h.data); setFiles(f.data); setMessages(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, chatBusy]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15MB"); return; }
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      await http.post(`/haki/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File terunggah");
      const { data } = await http.get(`/haki/${id}/files`); setFiles(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const delFile = async (fid) => {
    if (!confirm("Hapus file ini?")) return;
    await http.delete(`/haki/${id}/files/${fid}`);
    setFiles(files.filter(f => f.file_id !== fid));
  };

  const send = async (msgOverride) => {
    const msg = (msgOverride ?? chatInput).trim();
    if (!msg || chatBusy) return;
    // optimistic
    const tmpUser = { message_id: "tmp-" + Date.now(), role: "user", text: msg, created_at: new Date().toISOString() };
    setMessages([...messages, tmpUser]);
    setChatInput(""); setChatBusy(true);
    try {
      const { data } = await http.post(`/haki/${id}/chat`, { message: msg });
      setMessages(m => [...m.filter(x => x.message_id !== tmpUser.message_id), data.user_message, data.assistant_message]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI gagal menjawab");
      setMessages(m => m.filter(x => x.message_id !== tmpUser.message_id));
    } finally { setChatBusy(false); }
  };

  if (!haki) return <div className="text-slate-400 italic">Memuat…</div>;
  const stages = types[haki.haki_type]?.stages || [];
  const pct = stages.length > 1 ? Math.round((haki.current_stage / (stages.length - 1)) * 100) : 0;

  return (
    <div data-testid="haki-detail-page">
      <Link to="/haki" className="inline-flex items-center gap-1 text-sm text-[#D97706] font-semibold" data-testid="haki-back-btn"><ArrowLeft size={14}/> Kembali ke daftar HAKI</Link>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <Badge className={`${TYPE_COLOR[haki.haki_type]} text-white`}>{types[haki.haki_type]?.label}</Badge>
        <span className="text-xs mono text-slate-500">{haki.application_number || "belum ada no. permohonan"}</span>
      </div>
      <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1">{haki.title}</h1>
      {haki.description && <p className="text-slate-600 mt-1 max-w-3xl">{haki.description}</p>}

      <div className="kt-card p-5 mt-4">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2"><Milestone size={16} className="text-[#D97706]"/><b>Tahap saat ini:</b> {stages[haki.current_stage]}</div>
          <span className="font-semibold text-[#0F2922]">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-200 mt-2 overflow-hidden">
          <div className="h-full bg-[#D97706]" style={{ width: `${pct}%` }}/>
        </div>
      </div>

      <Tabs defaultValue="chat" className="mt-6">
        <TabsList>
          <TabsTrigger value="chat" data-testid="haki-tab-chat"><Bot size={14} className="mr-1"/> Konsultasi AI</TabsTrigger>
          <TabsTrigger value="files" data-testid="haki-tab-files"><FileText size={14} className="mr-1"/> Bukti & Dokumen ({files.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 kt-card p-0 overflow-hidden flex flex-col" style={{ minHeight: 500 }}>
              <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-[#0F2922] to-[#173E34] text-[#FAFA96] flex items-center gap-2">
                <Sparkles size={16}/> <span className="font-semibold">Konsultan HAKI (Claude Sonnet 5)</span>
                <Badge className="ml-auto bg-[#D97706] text-white border-0">Ahli Paten & Merek</Badge>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FAFA96]/20">
                {messages.length === 0 && !chatBusy && (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0F2922] text-[#FAFA96] flex items-center justify-center"><Bot size={26}/></div>
                    <p className="text-slate-600 mt-3">Tanyakan apa saja tentang klaim, prior art, atau strategi HAKI Anda.</p>
                  </div>
                )}
                {messages.map(m => (
                  <div key={m.message_id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role !== "user" && <div className="w-8 h-8 rounded-lg bg-[#0F2922] text-[#FAFA96] flex items-center justify-center shrink-0"><Bot size={16}/></div>}
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[#D97706] text-white" : "bg-white border border-slate-200 text-slate-800"}`} data-testid={m.role === "user" ? "chat-msg-user" : "chat-msg-assistant"}>
                      {m.role === "user" ? m.text : <div className="space-y-1 leading-relaxed">{md(m.text)}</div>}
                    </div>
                    {m.role === "user" && <div className="w-8 h-8 rounded-lg bg-[#D97706] text-white flex items-center justify-center shrink-0"><UserIcon size={16}/></div>}
                  </div>
                ))}
                {chatBusy && (
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#0F2922] text-[#FAFA96] flex items-center justify-center"><Bot size={16}/></div>
                    <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin"/> Konsultan sedang menganalisis…</div>
                  </div>
                )}
                <div ref={chatEndRef}/>
              </div>
              <div className="border-t border-slate-200 p-3 bg-white">
                <div className="flex gap-2">
                  <Textarea
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Tanya soal klaim, prior art, merek…"
                    rows={2}
                    className="resize-none"
                    disabled={chatBusy}
                    data-testid="haki-chat-input"
                  />
                  <Button onClick={() => send()} disabled={chatBusy || !chatInput.trim()} className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96] self-stretch" data-testid="haki-chat-send">
                    <Send size={16}/>
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Tekan Enter untuk kirim · Shift+Enter untuk baris baru</p>
              </div>
            </div>

            <div className="kt-card p-4">
              <h3 className="font-semibold text-[#0F2922] flex items-center gap-2"><Sparkles size={14} className="text-[#D97706]"/> Pertanyaan cepat</h3>
              <div className="space-y-2 mt-3">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} disabled={chatBusy} className="text-left w-full text-xs p-3 rounded-lg border border-slate-200 hover:border-[#D97706] hover:bg-[#FAFA96]/40 transition disabled:opacity-50" data-testid={`haki-suggest-${i}`}>{s}</button>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-[#FAFA96]/40 border border-[#D97706]/20 text-xs text-slate-600">
                <b className="text-[#0F2922]">Catatan:</b> AI memberi orientasi umum, bukan opini legal final. Konsultasikan dengan konsultan HAKI terdaftar untuk keputusan pengajuan.
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <div className="kt-card p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-semibold text-[#0F2922]">Sertifikat & Bukti Pendukung</h3>
                <p className="text-xs text-slate-500 mt-1">Simpan sertifikat, sketsa desain, deskripsi paten, surat pengalihan hak — maks 15MB / file (PDF, DOCX, PPTX, PNG, JPG, ZIP).</p>
              </div>
              <div>
                <input ref={fileRef} type="file" hidden onChange={onUpload}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.pptx,.zip,.txt"/>
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="haki-upload-btn">
                  {uploading ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Upload size={14} className="mr-2"/>}
                  {uploading ? "Mengunggah…" : "Unggah File"}
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
              {files.length === 0 && <div className="col-span-full text-center text-slate-400 italic py-8">Belum ada file terunggah.</div>}
              {files.map(f => {
                const isImg = f.content_type?.startsWith("image/");
                const url = `${API}/haki/${id}/files/${f.file_id}/download?auth=${encodeURIComponent(token || "")}`;
                return (
                  <div key={f.file_id} className="rounded-xl border border-slate-200 overflow-hidden bg-white hover:shadow-md transition group" data-testid="haki-file-card">
                    <div className="h-32 bg-[#F3F4F1] flex items-center justify-center overflow-hidden">
                      {isImg ? (
                        <img src={url} alt={f.original_filename} className="w-full h-full object-cover"/>
                      ) : (
                        <div className="text-[#0F2922] flex flex-col items-center gap-1">
                          <FileText size={40} strokeWidth={1.5}/>
                          <span className="mono text-[10px] uppercase">{(f.original_filename.split(".").pop() || "file").slice(0,5)}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-semibold text-[#0F2922] truncate" title={f.original_filename}>{f.original_filename}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{(f.size/1024).toFixed(1)} KB · {new Date(f.created_at).toLocaleDateString("id-ID")}</div>
                      <div className="flex items-center gap-1 mt-2">
                        <a href={url} target="_blank" rel="noreferrer" className="flex-1"><Button size="sm" variant="outline" className="w-full h-8"><Download size={12} className="mr-1"/>Buka</Button></a>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delFile(f.file_id)} data-testid={`haki-file-delete-${f.file_id}`}><Trash2 size={13}/></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
